import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import './Settings.css';

function Settings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    display_name: '',
    job_title: ''
  });

  // Fetch current user data
  useEffect(() => {
    const fetchUser = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;
        const userData = await apiService.getCurrentUser(idToken);
        setCurrentUser(userData);
        setFormData({
          display_name: userData.display_name || '',
          job_title: userData.job_title || ''
        });
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [auth.isAuthenticated, auth.user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear success message when user starts editing
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.display_name.trim()) {
      setError('Display name is required');
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const idToken = auth.user.id_token;
      const updates = {
        display_name: formData.display_name.trim(),
        job_title: formData.job_title.trim() || null
      };

      const updatedUser = await apiService.updateUser(idToken, updates);
      setCurrentUser(updatedUser);
      setSuccess(true);

      // Dispatch event to notify Layout component to refresh user data
      window.dispatchEvent(new Event('profileUpdated'));

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to current user data
    if (currentUser) {
      setFormData({
        display_name: currentUser.display_name || '',
        job_title: currentUser.job_title || ''
      });
    }
    setError(null);
    setSuccess(false);
  };

  if (loading) {
    return (
      <div className="settings-container">
        <LoadingSpinner message="Loading settings..." />
      </div>
    );
  }

  if (error && !currentUser) {
    return (
      <div className="settings-container">
        <ErrorDisplay
          title="Error Loading Settings"
          message={error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-subtitle">Manage your profile and preferences</p>
      </div>

      <div className="settings-content">
        <section className="settings-section">
          <h2 className="section-title">Profile Information</h2>

          {success && (
            <div className="success-message">
              <span className="success-icon">✓</span>
              Profile updated successfully!
            </div>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠</span>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="settings-form">
            <div className="form-group">
              <label htmlFor="display_name" className="form-label">
                Display Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your display name"
                required
              />
              <p className="form-help">This is how your name will appear to other users</p>
            </div>

            <div className="form-group">
              <label htmlFor="job_title" className="form-label">
                Job Title
              </label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={formData.job_title}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Software Engineer, Product Manager"
              />
              <p className="form-help">Your current job title or position (optional)</p>
            </div>

            <div className="form-group readonly">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={currentUser?.email || ''}
                className="form-input"
                disabled
              />
              <p className="form-help">Email cannot be changed</p>
            </div>

            <div className="form-group readonly">
              <label htmlFor="user_type" className="form-label">
                Account Type
              </label>
              <div className="user-type-display">
                <span className="type-icon">
                  {currentUser?.user_type === 'individual' ? '👤' :
                   currentUser?.user_type === 'developer' ? '💻' : '🏢'}
                </span>
                <span className="type-text">
                  {currentUser?.user_type === 'individual' ? 'Individual' :
                   currentUser?.user_type === 'developer' ? 'Developer' : 'Organization'}
                </span>
              </div>
              <p className="form-help">Account type cannot be changed</p>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="btn-cancel"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

export default Settings;
